import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { availabilityOptionNames } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeAvailability/availabilityModal/availabilityOptions';
import { FlowMessengerNodeAvailabilityStrategy } from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { flowNodeColor } from 'fable/flowEditor';
import { TextBreak } from 'fable/flowEditor/components/flowManager/canvas/node/textBreak';
import type { MessengerExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { AvailabilityModal } from '../availabilityModal';

interface MessengerAvailabilityPayload {
  query: { type: FlowMessengerNodeAvailabilityStrategy.NoAgentsAvailable };
  availableNextNodeId: string;
  notAvailableNextNodeId: string;
}

type AvailabilityNodeAttributes = GenericNodeAttributes<
  MessengerAvailabilityPayload,
  MessengerExtraData
>;

type AvailabilityNodeProps = GenericNodeProps<
  MessengerAvailabilityPayload,
  MessengerExtraData
>;

const connectionLabels = {
  notAvailableBranch: 'Not available',
  availableBranch: 'Available',
};

const translatePayload: AvailabilityNodeAttributes['translatePayload'] = ({
  payload,
}) => {
  return [
    {
      id: uuid(),
      label: connectionLabels.availableBranch,
      connection: payload.availableNextNodeId,
    },
    {
      id: uuid(),
      label: connectionLabels.notAvailableBranch,
      connection: payload.notAvailableNextNodeId,
    },
  ];
};

const updateConnection: AvailabilityNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const { payload } = node;

  const fieldToUpdate =
    connection.connectionLabel === connectionLabels.availableBranch
      ? 'availableNextNodeId'
      : 'notAvailableNextNodeId';

  return {
    ...node,
    payload: {
      ...payload,
      [fieldToUpdate]: connectionId,
    },
  };
};

export const AvailabilityNodeAttributes: AvailabilityNodeAttributes = {
  iconName: 'hide-strategy',
  label: 'Agent availability',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection,
  NodeContent: AvailabilityNode,
  ActionModal: AvailabilityModal,
};

export function AvailabilityNode({ node }: AvailabilityNodeProps): JSX.Element {
  const {
    payload: { query },
  } = node;

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextBreak>
            <Text weight="normal" text="Strategy:" />
            <Text
              weight="semibold"
              text={availabilityOptionNames[query.type]}
            />
          </TextBreak>
        ),
        [query],
      )}
    </TextContent>
  );
}
